import { axios } from '@axios';
import { useMutation, useQuery, MutationOptions } from 'react-query';
import { ExtractFnReturnType, QueryConfig } from '../../lib/react-query';

export type SubscriptionPlanDto = {
  id: number;
  code: string;
  name: string;
  priceMonthly: string | number;
  currency: string;
  billingInterval?: 'day' | 'week' | 'month' | 'year';
  billingIntervalCount?: number;
  maxAgents: number | null;
  maxMinutesPerMonth: number | null;
  maxSmsPerMonth: number | null;
  trialDurationDays?: number | null;
  /** Created automatically in Stripe when the plan is saved. Read-only in normal use. */
  stripePriceId?: string | null;
  /** Stable Stripe product the plan's prices hang off. Never sent by the client. */
  stripeProductId?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  maxSmsNumber?: number | null;
};

export type UserSubscriptionDto = {
  id: number;
  userId: number;
  planId: number;
  status: string;
  stripeSubscriptionId?: string | null;
  stripeCustomerId?: string | null;
  startedAt: string;
  endsAt?: string | null;
  cancelledAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  user?: {
    id: number;
    email: string;
    firstname?: string;
    lastname?: string;
  };
  plan?: SubscriptionPlanDto;
};

export const getAdminPlans = (): Promise<any> => {
  return axios.get('/admin/subscriptions/plans');
};

export const createAdminPlan = (data: Partial<SubscriptionPlanDto>): Promise<any> => {
  return axios.post('/admin/subscriptions/plans', data);
};

export const updateAdminPlan = ({ id, data }: { id: number; data: Partial<SubscriptionPlanDto> }): Promise<any> => {
  return axios.patch(`/admin/subscriptions/plans/${id}`, data);
};

export const deleteAdminPlan = ({ id }: { id: number }): Promise<any> => {
  return axios.delete(`/admin/subscriptions/plans/${id}`);
};

/**
 * Every Stripe price this plan has ever used.
 *
 * Superseded prices matter operationally, not just historically: customers who
 * subscribed before a reprice are still billing on the old one.
 */
export type PlanPriceHistoryDto = {
  id: number;
  planId: number;
  stripePriceId: string;
  stripeProductId?: string | null;
  amount: string | number;
  currency: string;
  interval?: string | null;
  intervalCount?: number | null;
  activeFrom: string;
  archivedAt?: string | null;
};

export const getPlanPriceHistory = ({ id }: { id: number }): Promise<any> => {
  return axios.get(`/admin/subscriptions/plans/${id}/price-history`);
};

type GetPlanPriceHistoryFn = typeof getPlanPriceHistory;
export const usePlanPriceHistoryQuery = ({
  id,
  config,
}: {
  id: number | null;
  config?: QueryConfig<GetPlanPriceHistoryFn>;
}) => {
  return useQuery<ExtractFnReturnType<GetPlanPriceHistoryFn>>({
    queryKey: ['adminPlanPriceHistory', id],
    queryFn: () => getPlanPriceHistory({ id: id as number }),
    enabled: !!id,
    select: (res: any) => res,
    ...config,
  });
};

export const getUserSubscriptions = (params: {
  page: number;
  limit: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: string;
  startDate?: string;
  endDate?: string;
}): Promise<any> => {
  return axios.get('/admin/subscriptions/user-subscriptions', { params });
};

export const assignPlanToUser = ({
  userId,
  planId,
  endsAt,
  reason,
}: {
  userId: number;
  planId: number;
  endsAt?: string;
  reason?: string;
}): Promise<any> => {
  return axios.post(`/admin/subscriptions/users/${userId}/assign-plan`, { planId, endsAt, reason });
};

export const suspendUserSubscription = ({ userId, reason }: { userId: number; reason?: string }): Promise<any> => {
  return axios.post(`/admin/subscriptions/users/${userId}/suspend`, { reason });
};

export const reactivateUserSubscription = ({ userId }: { userId: number }): Promise<any> => {
  return axios.post(`/admin/subscriptions/users/${userId}/reactivate`);
};

type GetAdminPlansFn = typeof getAdminPlans;
export const useAdminPlansQuery = ({ config }: { config?: QueryConfig<GetAdminPlansFn> } = {}) => {
  return useQuery<ExtractFnReturnType<GetAdminPlansFn>>({
    queryKey: ['adminSubscriptionPlans'],
    queryFn: () => getAdminPlans(),
    select: (res: any) => res,
    ...config,
  });
};

type GetUserSubscriptionsFn = typeof getUserSubscriptions;
export const useUserSubscriptionsQuery = ({
  params,
  config,
}: {
  params: {
    page: number;
    limit: number;
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
    startDate?: string;
    endDate?: string;
  };
  config?: QueryConfig<GetUserSubscriptionsFn>;
}) => {
  return useQuery<ExtractFnReturnType<GetUserSubscriptionsFn>>({
    queryKey: ['adminUserSubscriptions', params],
    queryFn: () => getUserSubscriptions(params),
    select: (res: any) => res,
    ...config,
  });
};

export const useCreateAdminPlanMutation = (
  options: MutationOptions<Awaited<ReturnType<typeof createAdminPlan>>, unknown, Parameters<typeof createAdminPlan>[0]>
) => {
  return useMutation({
    mutationFn: createAdminPlan,
    ...options,
  });
};

export const useUpdateAdminPlanMutation = (
  options: MutationOptions<Awaited<ReturnType<typeof updateAdminPlan>>, unknown, Parameters<typeof updateAdminPlan>[0]>
) => {
  return useMutation({
    mutationFn: updateAdminPlan,
    ...options,
  });
};

export const useDeleteAdminPlanMutation = (
  options: MutationOptions<Awaited<ReturnType<typeof deleteAdminPlan>>, unknown, Parameters<typeof deleteAdminPlan>[0]>
) => {
  return useMutation({
    mutationFn: deleteAdminPlan,
    ...options,
  });
};

export const useAssignPlanToUserMutation = (
  options: MutationOptions<
    Awaited<ReturnType<typeof assignPlanToUser>>,
    unknown,
    Parameters<typeof assignPlanToUser>[0]
  >
) => {
  return useMutation({
    mutationFn: assignPlanToUser,
    ...options,
  });
};

export const useSuspendUserSubscriptionMutation = (
  options: MutationOptions<
    Awaited<ReturnType<typeof suspendUserSubscription>>,
    unknown,
    Parameters<typeof suspendUserSubscription>[0]
  >
) => {
  return useMutation({
    mutationFn: suspendUserSubscription,
    ...options,
  });
};

export const useReactivateUserSubscriptionMutation = (
  options: MutationOptions<
    Awaited<ReturnType<typeof reactivateUserSubscription>>,
    unknown,
    Parameters<typeof reactivateUserSubscription>[0]
  >
) => {
  return useMutation({
    mutationFn: reactivateUserSubscription,
    ...options,
  });
};

export type SubscriptionAddonDto = {
  id: number;
  code: string;
  name: string;
  price: string | number;
  currency: string;
  type: 'minutes' | 'sms' | 'agent' | 'other';
  quantity: number | null;
  /** Created automatically in Stripe when the add-on is saved. */
  stripePriceId?: string | null;
  stripeProductId?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export const getAdminAddons = (): Promise<any> => {
  return axios.get('/admin/subscriptions/addons');
};

export const createAdminAddon = (data: Partial<SubscriptionAddonDto>): Promise<any> => {
  return axios.post('/admin/subscriptions/addons', data);
};

export const updateAdminAddon = ({ id, data }: { id: number; data: Partial<SubscriptionAddonDto> }): Promise<any> => {
  return axios.patch(`/admin/subscriptions/addons/${id}`, data);
};

export const deleteAdminAddon = ({ id }: { id: number }): Promise<any> => {
  return axios.delete(`/admin/subscriptions/addons/${id}`);
};

type GetAdminAddonsFn = typeof getAdminAddons;
export const useAdminAddonsQuery = ({ config }: { config?: QueryConfig<GetAdminAddonsFn> } = {}) => {
  return useQuery<ExtractFnReturnType<GetAdminAddonsFn>>({
    queryKey: ['adminSubscriptionAddons'],
    queryFn: () => getAdminAddons(),
    select: (res: any) => res,
    ...config,
  });
};

export const useCreateAdminAddonMutation = (
  options: MutationOptions<
    Awaited<ReturnType<typeof createAdminAddon>>,
    unknown,
    Parameters<typeof createAdminAddon>[0]
  >
) => {
  return useMutation({
    mutationFn: createAdminAddon,
    ...options,
  });
};

export const useUpdateAdminAddonMutation = (
  options: MutationOptions<
    Awaited<ReturnType<typeof updateAdminAddon>>,
    unknown,
    Parameters<typeof updateAdminAddon>[0]
  >
) => {
  return useMutation({
    mutationFn: updateAdminAddon,
    ...options,
  });
};

export const useDeleteAdminAddonMutation = (
  options: MutationOptions<
    Awaited<ReturnType<typeof deleteAdminAddon>>,
    unknown,
    Parameters<typeof deleteAdminAddon>[0]
  >
) => {
  return useMutation({
    mutationFn: deleteAdminAddon,
    ...options,
  });
};

export type RefundRequestDto = {
  id: number;
  userId: number;
  stripeSubscriptionId: string;
  stripePaymentIntentId: string;
  stripeChargeId: string;
  stripeRefundId?: string | null;
  amount: number;
  currency: string;
  reason: string;
  status: 'pending' | 'processing' | 'refunded' | 'rejected' | 'failed';
  adminComment?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    email: string;
    firstname?: string;
    lastname?: string;
  };
};

export const getRefundRequests = (params: { page: number; limit: number }): Promise<any> => {
  return axios.get('/admin/subscriptions/refund-requests', { params });
};

export const resolveRefundRequest = ({
  id,
  action,
  comment,
}: {
  id: number;
  action: 'approve' | 'reject';
  comment?: string;
}): Promise<any> => {
  return axios.post(`/admin/subscriptions/refund-requests/${id}/resolve`, { action, comment });
};

type GetRefundRequestsFn = typeof getRefundRequests;
export const useRefundRequestsQuery = ({
  params,
  config,
}: {
  params: { page: number; limit: number };
  config?: QueryConfig<GetRefundRequestsFn>;
}) => {
  return useQuery<ExtractFnReturnType<GetRefundRequestsFn>>({
    queryKey: ['adminRefundRequests', params],
    queryFn: () => getRefundRequests(params),
    select: (res: any) => res,
    ...config,
  });
};

export const useResolveRefundRequestMutation = (
  options: MutationOptions<
    Awaited<ReturnType<typeof resolveRefundRequest>>,
    unknown,
    Parameters<typeof resolveRefundRequest>[0]
  >
) => {
  return useMutation({
    mutationFn: resolveRefundRequest,
    ...options,
  });
};

// USER-SIDE APIS
export const getMySubscriptionHistory = (): Promise<any> => {
  return axios.get('/subscriptions/me/history');
};

export const getMyInvoices = (): Promise<any> => {
  return axios.get('/subscriptions/me/invoices');
};

export const getMyPaymentMethods = (): Promise<any> => {
  return axios.get('/subscriptions/me/payment-methods');
};

export const setMyDefaultPaymentMethod = ({ paymentMethodId }: { paymentMethodId: string }): Promise<any> => {
  return axios.post('/subscriptions/me/payment-methods/default', { paymentMethodId });
};

export const removeMyPaymentMethod = ({ id }: { id: string }): Promise<any> => {
  return axios.delete(`/subscriptions/me/payment-methods/${id}`);
};

export const getMySpending = (): Promise<any> => {
  return axios.get('/subscriptions/me/spending');
};

export const getMyNextBilling = (): Promise<any> => {
  return axios.get('/subscriptions/me/next-billing');
};

export const getMyPrivileges = (): Promise<any> => {
  return axios.get('/subscriptions/me/privileges');
};

export const getMySubscription = (): Promise<any> => {
  return axios.get('/subscriptions/me');
};

export const getMyUsage = (): Promise<any> => {
  return axios.get('/subscriptions/me/usage');
};

export const getBillingPortal = ({ returnUrl }: { returnUrl: string }): Promise<any> => {
  return axios.post('/subscriptions/me/billing-portal', { returnUrl });
};

export const cancelMySubscription = (): Promise<any> => {
  return axios.post('/subscriptions/me/cancel');
};

export const resumeMySubscription = (): Promise<any> => {
  return axios.post('/subscriptions/me/resume');
};

// User hooks
export const useMySubscriptionHistoryQuery = (config?: QueryConfig<typeof getMySubscriptionHistory>) => {
  return useQuery({
    queryKey: ['mySubscriptionHistory'],
    queryFn: getMySubscriptionHistory,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useMyInvoicesQuery = (config?: QueryConfig<typeof getMyInvoices>) => {
  return useQuery({
    queryKey: ['myInvoices'],
    queryFn: getMyInvoices,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useMyPaymentMethodsQuery = (config?: QueryConfig<typeof getMyPaymentMethods>) => {
  return useQuery({
    queryKey: ['myPaymentMethods'],
    queryFn: getMyPaymentMethods,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useMySpendingQuery = (config?: QueryConfig<typeof getMySpending>) => {
  return useQuery({
    queryKey: ['mySpending'],
    queryFn: getMySpending,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useMyNextBillingQuery = (config?: QueryConfig<typeof getMyNextBilling>) => {
  return useQuery({
    queryKey: ['myNextBilling'],
    queryFn: getMyNextBilling,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useMyPrivilegesQuery = (config?: QueryConfig<typeof getMyPrivileges>) => {
  return useQuery({
    queryKey: ['myPrivileges'],
    queryFn: getMyPrivileges,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useMySubscriptionQuery = (config?: QueryConfig<typeof getMySubscription>) => {
  return useQuery({
    queryKey: ['mySubscription'],
    queryFn: getMySubscription,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useMyUsageQuery = (config?: QueryConfig<typeof getMyUsage>) => {
  return useQuery({
    queryKey: ['myUsage'],
    queryFn: getMyUsage,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useSetMyDefaultPaymentMethodMutation = (options?: MutationOptions<any, unknown, { paymentMethodId: string }>) => {
  return useMutation({
    mutationFn: setMyDefaultPaymentMethod,
    ...options,
  });
};

export const useRemoveMyPaymentMethodMutation = (options?: MutationOptions<any, unknown, { id: string }>) => {
  return useMutation({
    mutationFn: removeMyPaymentMethod,
    ...options,
  });
};

export const useBillingPortalMutation = (options?: MutationOptions<any, unknown, { returnUrl: string }>) => {
  return useMutation({
    mutationFn: getBillingPortal,
    ...options,
  });
};

export const useCancelMySubscriptionMutation = (options?: MutationOptions<any, unknown, void>) => {
  return useMutation({
    mutationFn: cancelMySubscription,
    ...options,
  });
};

export const useResumeMySubscriptionMutation = (options?: MutationOptions<any, unknown, void>) => {
  return useMutation({
    mutationFn: resumeMySubscription,
    ...options,
  });
};

// ADMIN-SIDE NEW APIS
export const getAdminStats = (): Promise<any> => {
  return axios.get('/admin/subscriptions/stats');
};

export const grantGoodwillCredits = ({ userId, resourceType, amount, reason }: { userId: number; resourceType: 'minutes' | 'sms' | 'agents' | 'sms_numbers'; amount: number; reason?: string }): Promise<any> => {
  return axios.post(`/admin/subscriptions/users/${userId}/goodwill-credits`, { resourceType, amount, reason });
};

export const extendSubscription = ({ userId, days, reason }: { userId: number; days: number; reason?: string }): Promise<any> => {
  return axios.post(`/admin/subscriptions/users/${userId}/extend`, { days, reason });
};

export const extendTrial = ({ userId, days, reason }: { userId: number; days: number; reason?: string }): Promise<any> => {
  return axios.post(`/admin/subscriptions/users/${userId}/extend-trial`, { days, reason });
};

export const grantTrialAgain = ({ userId, days, reason }: { userId: number; days: number; reason?: string }): Promise<any> => {
  return axios.post(`/admin/subscriptions/users/${userId}/grant-trial-again`, { days, reason });
};

export const getUserSpending = ({ userId }: { userId: number }): Promise<any> => {
  return axios.get(`/admin/subscriptions/users/${userId}/spending`);
};

export const getUserInvoices = ({ userId }: { userId: number }): Promise<any> => {
  return axios.get(`/admin/subscriptions/users/${userId}/invoices`);
};

export const getUserSubscriptionHistory = ({ userId }: { userId: number }): Promise<any> => {
  return axios.get(`/admin/subscriptions/users/${userId}/history`);
};

// React-Query hooks for admin
export const useAdminStatsQuery = (config?: QueryConfig<typeof getAdminStats>) => {
  return useQuery({
    queryKey: ['adminStats'],
    queryFn: getAdminStats,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useGrantGoodwillCreditsMutation = (options?: MutationOptions<any, unknown, Parameters<typeof grantGoodwillCredits>[0]>) => {
  return useMutation({
    mutationFn: grantGoodwillCredits,
    ...options,
  });
};

export const useExtendSubscriptionMutation = (options?: MutationOptions<any, unknown, Parameters<typeof extendSubscription>[0]>) => {
  return useMutation({
    mutationFn: extendSubscription,
    ...options,
  });
};

export const useExtendTrialMutation = (options?: MutationOptions<any, unknown, Parameters<typeof extendTrial>[0]>) => {
  return useMutation({
    mutationFn: extendTrial,
    ...options,
  });
};

export const useGrantTrialAgainMutation = (options?: MutationOptions<any, unknown, Parameters<typeof grantTrialAgain>[0]>) => {
  return useMutation({
    mutationFn: grantTrialAgain,
    ...options,
  });
};

export const useUserSpendingQuery = ({ userId, config }: { userId: number; config?: QueryConfig<typeof getUserSpending> }) => {
  return useQuery({
    queryKey: ['userSpending', userId],
    queryFn: () => getUserSpending({ userId }),
    enabled: !!userId,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useUserInvoicesQuery = ({ userId, config }: { userId: number; config?: QueryConfig<typeof getUserInvoices> }) => {
  return useQuery({
    queryKey: ['userInvoices', userId],
    queryFn: () => getUserInvoices({ userId }),
    enabled: !!userId,
    select: (res: any) => res.data,
    ...config,
  });
};

export const useUserSubscriptionHistoryQuery = ({ userId, config }: { userId: number; config?: QueryConfig<typeof getUserSubscriptionHistory> }) => {
  return useQuery({
    queryKey: ['userSubscriptionHistory', userId],
    queryFn: () => getUserSubscriptionHistory({ userId }),
    enabled: !!userId,
    select: (res: any) => res.data,
    ...config,
  });
};

export const getUserUsage = ({ userId }: { userId: number }): Promise<any> => {
  return axios.get(`/admin/subscriptions/users/${userId}/usage`);
};

export const useUserUsageQuery = ({ userId, config }: { userId: number; config?: QueryConfig<typeof getUserUsage> }) => {
  return useQuery({
    queryKey: ['userUsage', userId],
    queryFn: () => getUserUsage({ userId }),
    enabled: !!userId,
    select: (res: any) => res.data,
    ...config,
  });
};
